/**
 * "What you have on <Plan>" — the value half of the lapsed paywall for a PAID account whose
 * payment lapsed. The expired-trial copy keeps its own curated list (`LapsedProPlusFeatures`).
 *
 * The rows ARE the subscription wizard's plan-card bullets (`wizard/plan-feature-copy.ts`), so
 * what the user reads here matches the card for the plan they're paying for. Same row styling as
 * `LapsedProPlusFeatures`. Renders nothing for a plan with no curated list (Free, legacy Pro,
 * Enterprise) — the popup then reads as it did before this list existed.
 */
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { getPlanFeatureCopy } from '@/features/billing/dialogs/wizard/plan-feature-copy';
import { PLAN_TEXT_FEATURES } from '@scio3/shared-types';

type Props = {
  /** `company.payment.planFeature`, e.g. `standard`, `proPlus`, `engage`. */
  planFeature: string;
};

/** Display name for a plan alias, case-insensitive: payment docs say `proplus`, the map `proPlus`. */
function planDisplayName(planFeature: string): string {
  const key = Object.keys(PLAN_TEXT_FEATURES).find(
    (k) => k.toLowerCase() === planFeature.toLowerCase(),
  );
  return key ? PLAN_TEXT_FEATURES[key] : planFeature;
}

export function LapsedPlanFeatures({ planFeature }: Props) {
  const { t } = useTranslation();
  const rows = getPlanFeatureCopy(planFeature);
  if (!rows?.length) return null;

  return (
    <div className="space-y-3" data-testid="lapsed-plan-features">
      <p className="text-base font-semibold text-foreground">
        {t('What you have on {{plan}}', { plan: t(planDisplayName(planFeature)) })}
      </p>
      <ul className="space-y-3">
        {rows.map((row) => {
          const Icon = row.icon ?? Check;
          return (
            <li key={row.label} className="flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                <Icon className="h-4 w-4 text-foreground" aria-hidden="true" />
              </span>
              <span className="text-sm text-foreground">{t(row.label)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
